import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useId, useRef } from "react";
import { useFormContext } from "react-hook-form";

type Props = {
  name: string;
  disabled?: boolean | string | React.ReactNode;
  readOnly?: boolean | string | React.ReactNode;
  label?: string;
  options: {
    value: string;
    label: string;
    icon?: React.ElementType | "both";
  }[];
  autoFocus?: boolean;
  placeholder?: string;
  description?: string;
  onFocus?: (value: string) => void;
  className?: React.HTMLAttributes<HTMLButtonElement>["className"];
  formItemClassName?: string;
  children?: (
    option:
      | { value: string; label: string; icon?: React.ElementType | "both" }
      | undefined,
  ) => React.ReactNode;
};

export const SelectInput = ({
  label,
  name,
  options,
  disabled = false,
  readOnly = false,
  className,
  formItemClassName,
  children,
  description,
  placeholder,
}: Props) => {
  const form = useFormContext();
  const { error } = useFormField();
  const ref = useRef(null);
  const id = useId();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const handleValueChange = (value: string) => {
          field.onChange(value);
          setTimeout(() => {
            const element = document.getElementById(`${id}-select`);

            element?.blur();
          }, 100);
        };

        const currentOption = options.find(
          (option) => option.value === field.value,
        );

        return (
          <FormItem className={formItemClassName}>
            {label && <FormLabel>{label}</FormLabel>}
            <Select onValueChange={handleValueChange} value={field.value}>
              <FormControl>
                <SelectTrigger
                  id={`${id}-select`}
                  disabled={disabled || readOnly}
                  className={cn(
                    className,
                    Boolean(readOnly) &&
                      "cursor-not-allowed disabled:opacity-100",
                  )}
                >
                  {children ? (
                    children(currentOption)
                  ) : (
                    <SelectValue
                      placeholder={placeholder}
                      aria-invalid={error ? true : undefined}
                      ref={ref}
                    />
                  )}
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      {option.icon && option.icon !== "both" && (
                        <option.icon className="h-4 w-4" />
                      )}
                      {option.icon === "both" && (
                        <div className="flex items-center h-4 w-4 justify-center">
                          <span className="text-sm font-medium">±</span>
                        </div>
                      )}
                      <span>{option.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
